/**
 * PhotosSection Component
 * Photo gallery for daily logs
 * BuilderBel Construction Project Management System
 */

import { Camera, ChevronDown, ChevronRight, Image, Trash2, X, ZoomIn } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { Control, Controller, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import type { DailyLogPhoto, PhotoCategory } from '../../../types/dailyLog';
import { Dropdown, Input } from '../../forms';

// Photo category options
const PHOTO_CATEGORY_OPTIONS: { value: PhotoCategory; label: string }[] = [
  { value: 'progress', label: 'Progress' },
  { value: 'safety', label: 'Safety' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'weather', label: 'Weather' },
  { value: 'issue', label: 'Issue' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_COLORS: Record<PhotoCategory, string> = {
  progress: 'bg-blue-100 text-blue-800',
  safety: 'bg-green-100 text-green-800',
  delivery: 'bg-purple-100 text-purple-800',
  weather: 'bg-cyan-100 text-cyan-800',
  issue: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-800',
};

interface PhotosSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'photos', 'id'>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
  onUploadPhotos?: (files: File[]) => Promise<{ url: string; s3Key: string }[]>;
}

// Photo lightbox component
const PhotoLightbox: React.FC<{
  photo: Partial<DailyLogPhoto>;
  onClose: () => void;
}> = ({ photo, onClose }) => (
  <div
    className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4"
    onClick={onClose}
  >
    <button
      type="button"
      onClick={onClose}
      className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20"
    >
      <X className="h-6 w-6" />
    </button>
    <div className="max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
      <img
        src={photo.url}
        alt={photo.caption || 'Daily log photo'}
        className="max-h-[80vh] object-contain rounded-lg"
      />
      {(photo.caption || photo.location) && (
        <div className="mt-4 text-white text-center">
          {photo.caption && <p className="text-lg">{photo.caption}</p>}
          {photo.location && <p className="text-sm text-gray-300">{photo.location}</p>}
        </div>
      )}
    </div>
  </div>
);

export const PhotosSection: React.FC<PhotosSectionProps> = ({
  control,
  errors,
  fieldArray,
  isExpanded,
  onToggle,
  disabled = false,
  onUploadPhotos,
}) => {
  const { fields, append, remove } = fieldArray;
  const photoErrors = errors.photos as unknown as FieldErrors<DailyLogPhoto>[] | undefined;
  const [lightboxPhoto, setLightboxPhoto] = useState<Partial<DailyLogPhoto> | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Handle file selection
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0 || !onUploadPhotos) return;

      setIsUploading(true);
      try {
        const uploadedPhotos = await onUploadPhotos(files);
        uploadedPhotos.forEach(photo => {
          append({
            url: photo.url,
            s3Key: photo.s3Key,
            caption: '',
            location: '',
            category: 'progress',
            takenAt: new Date().toISOString(),
            takenBy: '', // Will be set from user context
          });
        });
      } catch (error) {
        console.error('Failed to upload photos:', error);
      } finally {
        setIsUploading(false);
        // Reset input
        e.target.value = '';
      }
    },
    [append, onUploadPhotos]
  );

  // Handle drag and drop
  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      if (files.length === 0 || !onUploadPhotos) return;

      setIsUploading(true);
      try {
        const uploadedPhotos = await onUploadPhotos(files);
        uploadedPhotos.forEach(photo => {
          append({
            url: photo.url,
            s3Key: photo.s3Key,
            caption: '',
            location: '',
            category: 'progress',
            takenAt: new Date().toISOString(),
            takenBy: '',
          });
        });
      } catch (error) {
        console.error('Failed to upload photos:', error);
      } finally {
        setIsUploading(false);
      }
    },
    [append, onUploadPhotos]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Count by category
  const categoryCounts = fields.reduce(
    (acc, _, index) => {
      const category = (control._formValues?.photos?.[index]?.category as PhotoCategory) || 'other';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    },
    {} as Record<PhotoCategory, number>
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Camera className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Photos</span>
          <span className="text-sm text-gray-500">
            ({fields.length} {fields.length === 1 ? 'photo' : 'photos'})
          </span>
        </div>
        <div className="flex items-center space-x-4">
          {/* Category summary */}
          {fields.length > 0 && (
            <div className="flex items-center space-x-1">
              {Object.entries(categoryCounts).map(([category, count]) => (
                <span
                  key={category}
                  className={`px-1.5 py-0.5 text-xs rounded ${CATEGORY_COLORS[category as PhotoCategory]}`}
                >
                  {count}
                </span>
              ))}
            </div>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Upload Area */}
          {!disabled && onUploadPhotos && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isUploading
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-gray-300 hover:border-brand-500 hover:bg-gray-50'
              }`}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="photo-upload"
                disabled={isUploading}
              />
              <label htmlFor="photo-upload" className="cursor-pointer flex flex-col items-center">
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-2" />
                    <span className="text-sm text-brand-600">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Camera className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      Drop photos here or click to upload
                    </span>
                    <span className="text-xs text-gray-400 mt-1">
                      Supports JPG, PNG, GIF up to 10MB each
                    </span>
                  </>
                )}
              </label>
            </div>
          )}

          {/* Photo Grid */}
          {fields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {fields.map((field, index) => {
                const photoUrl = control._formValues?.photos?.[index]?.url as string;
                const photoCategory = control._formValues?.photos?.[index]
                  ?.category as PhotoCategory;

                return (
                  <div
                    key={field.id}
                    className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                  >
                    {/* Photo Thumbnail */}
                    <div className="aspect-video relative">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <Image className="h-12 w-12 text-gray-300" />
                        </div>
                      )}

                      {/* Overlay Actions */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                        <button
                          type="button"
                          onClick={() => setLightboxPhoto(control._formValues?.photos?.[index])}
                          className="p-2 bg-white/20 rounded-full text-white hover:bg-white/30"
                          title="View full size"
                        >
                          <ZoomIn className="h-5 w-5" />
                        </button>
                        {!disabled && (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="p-2 bg-red-500/80 rounded-full text-white hover:bg-red-600"
                            title="Remove photo"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                      </div>

                      {/* Category Badge */}
                      {photoCategory && (
                        <span
                          className={`absolute top-2 left-2 px-2 py-0.5 text-xs font-medium rounded ${CATEGORY_COLORS[photoCategory]}`}
                        >
                          {PHOTO_CATEGORY_OPTIONS.find(o => o.value === photoCategory)?.label}
                        </span>
                      )}
                    </div>

                    {/* Photo Details */}
                    <div className="p-3 space-y-2">
                      <Controller
                        name={`photos.${index}.category`}
                        control={control}
                        render={({ field: inputField }) => (
                          <Dropdown
                            label="Category"
                            options={PHOTO_CATEGORY_OPTIONS}
                            value={inputField.value}
                            onChange={inputField.onChange}
                            error={photoErrors?.[index]?.category?.message}
                            disabled={disabled}
                          />
                        )}
                      />

                      <Controller
                        name={`photos.${index}.caption`}
                        control={control}
                        render={({ field: inputField }) => (
                          <Input
                            label="Caption"
                            value={inputField.value || ''}
                            onChange={inputField.onChange}
                            error={photoErrors?.[index]?.caption?.message}
                            disabled={disabled}
                            placeholder="Describe the photo..."
                          />
                        )}
                      />

                      <Controller
                        name={`photos.${index}.location`}
                        control={control}
                        render={({ field: inputField }) => (
                          <Input
                            label="Location"
                            value={inputField.value || ''}
                            onChange={inputField.onChange}
                            error={photoErrors?.[index]?.location?.message}
                            disabled={disabled}
                            placeholder="Where was this taken?"
                          />
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {fields.length === 0 && disabled && (
            <div className="text-center py-8 text-gray-500">
              <Camera className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No photos attached</p>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <PhotoLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />
      )}
    </div>
  );
};

export default PhotosSection;
